import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import GridTypeA from "components/grids/GridTypeA/GridTypeA"
import useSiteUploadActions from "components/useSiteUploadActions/useSiteUploadActions"
import { Panel } from "components-design-system"
import { REGENERATE_TASKS } from "constants/tasks"

import "./ProtocolMaintenance.scss"

// cell renderers
import Compare from "../CellRenderers/Compare"

// context
import useAppState from "context/hooks/useAppState"
import useAuthoringStore from "../../../Authoring/hooks/stores/useAuthoringStore"

// api
import { useUserPermissions } from "api/hooks"
import { useQueryClient } from "@tanstack/react-query"
import queryKeys from "api/utils/queryKeys"
import { useGetAuthoringStudy } from "api/hooks/studies/useAuthoringApi"
import {
  usePostPrsProtocolDownload,
  useGetPrsProtocolUpload,
  usePostPrsProtocolUpload,
} from "api/hooks/studies/useRegistries"
import { PRS_UPLOAD_TASKS } from "../../../../../../../constants/tasks"

const ProtocolMaintenance = ({ study, protocolDataCompare }) => {
  const { studyId } = useParams()
  const queryClient = useQueryClient()
  const { setPollingTaskId, setTaskType, pollingCallbackRef } = useAppState()

  const authoringId = useAuthoringStore((state) => state.authoringId)

  const { data: authoring } = useGetAuthoringStudy(authoringId)
  const { data: permissions } = useUserPermissions()

  const [queueId, setQueueId] = useState(null)

  const postPrsProtocolDownload = usePostPrsProtocolDownload(study?.authoring_study_id)

  const { data: pollingData } = useGetPrsProtocolUpload(
    { queueId },
    {
      refetchInterval: queueId ? 1000 : null,
      enabled: queueId ? true : false,
    }
  )

  useEffect(() => {
    if (pollingData?.in_progress === false || pollingData?.is_cancelled === true) {
      onStopPolling()
    }
  }, [pollingData?.in_progress])

  const gridOptions = {
    columnDefs: [
      {
        headerName: "Field",
        field: "field_label",
      },
      {
        headerName: "Prime",
        field: "prime_val",
        cellRenderer: Compare,
      },
      {
        headerName: "CTG",
        field: "prs_val",
        cellRenderer: Compare,
      },
    ],
    defaultColDef: {
      wrapText: true,
      filter: true,
      filterParams: {
        buttons: ["clear"],
      },
      enablePivot: true,
      enableCharts: false,
      enableRowGroup: false,
      cellStyle: { whiteSpace: "normal !important" },
      autoHeight: true,
    },
  }

  const onGridReady = (params) => {
    params.api.sizeColumnsToFit()
  }

  const onRegenerate = () => {
    postPrsProtocolDownload(
      {},
      {
        onSuccess: (resp) => {
          setPollingTaskId(resp?.data?.task_id)
          setTaskType(REGENERATE_TASKS.studyRegistry)
          pollingCallbackRef.current = (pollingData) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.registriesData.details(studyId) })
            queryClient.invalidateQueries({
              queryKey: queryKeys.registriesDataCompare.details(study?.authoring_study_id),
            })
          }
        },
      }
    )
  }

  const onStopPolling = () => {
    setQueueId(null)
  }

  const { RegenerateButton, UploadButton, UploadModals } = useSiteUploadActions({
    prsUploadStudyList: [Number(study?.authoring_study_id)],
    useOnGetPrsUpload: useGetPrsProtocolUpload,
    useOnPostPrsUpload: usePostPrsProtocolUpload,
    canUploadToPRS: permissions?.["study.protocolmaintenance.upload"],
    onRegenerate: onRegenerate,
    metadata: {},
    source: "study-registry",
    lastImportDate: authoring?.ctg_protocol_download_date,
    type: PRS_UPLOAD_TASKS.studyRegistryPrsProtocolUpload,
  })

  const actions = [RegenerateButton]
  if (permissions?.["study.protocolmaintenance.upload"]) {
    actions.push(UploadButton)
  }

  return (
    <Panel
      title="PROTOCOL MAINTENANCE"
      iconClassName="fa-light fa-hammer"
      style={{ flex: 2 }}
      actions={actions}
    >
      <div
        className="ag-theme-quartz reg-pm-grid-wrap"
        id="ag-theme-prime-disabled"
      >
        <GridTypeA
          onGridReady={onGridReady}
          gridOptions={gridOptions}
          domLayout="autoHeight"
          rowData={protocolDataCompare || []}
          className="study-data-protocol-maintenance"
          autoSizeStrategy="fitGridWidth"
          showRowCount={false}
        />
      </div>
      {UploadModals}
    </Panel>
  )
}

export default ProtocolMaintenance
